// src/hooks/useLocationSearch.js
import { useState, useCallback } from 'react';
import { searchLocation } from '../services/api/location';

const useLocationSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Only updates the text state — no API call per keystroke (Nominatim usage policy
  // requires an explicit search action, not live autocomplete).
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setSelectedIndex(-1);
  }, []);

  const runSearch = useCallback(async () => {
    const query = searchTerm.trim();
    if (!query) return;

    setLoading(true);
    setError(null);
    try {
      const data = await searchLocation(query);
      setResults(Array.isArray(data.results) ? data.results : []);
      setShowResults(true);
      setSelectedIndex(-1);
    } catch (err) {
      setError(err?.response?.data?.message || 'Location search failed');
      setResults([]);
      setShowResults(true);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (showResults && selectedIndex >= 0 && selectedIndex < results.length) {
        return results[selectedIndex];
      }
      runSearch();
      return null;
    }

    if (!showResults || results.length === 0) return null;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Escape':
        event.preventDefault();
        setShowResults(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
    return null;
  }, [showResults, results, selectedIndex, runSearch]);

  const selectResult = useCallback((result) => {
    setSearchTerm(result.displayName || '');
    setShowResults(false);
    setSelectedIndex(-1);
    return result;
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setResults([]);
    setShowResults(false);
    setSelectedIndex(-1);
    setError(null);
  }, []);

  const hideResults = useCallback(() => {
    setShowResults(false);
    setSelectedIndex(-1);
  }, []);

  const showResultsDropdown = useCallback(() => {
    if (results.length > 0) setShowResults(true);
  }, [results]);

  return {
    searchTerm,
    results,
    selectedIndex,
    showResults,
    loading,
    error,

    handleSearchChange,
    runSearch,
    handleKeyDown,
    selectResult,
    clearSearch,
    hideResults,
    showResultsDropdown,
  };
};

export default useLocationSearch;
